"use client";

import type { MutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import gql from "graphql-tag";
import type {
  CanvasSchedulerResponse,
  CreateCanvasFollowUpAppointmentInput,
} from "@rmh/defs";
import type { GeneratedMutation } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

interface CreateCanvasFollowUpAppointmentVariables {
  input: CreateCanvasFollowUpAppointmentInput;
}

interface CreateCanvasFollowUpAppointmentResponse {
  data?: {
    createCanvasFollowUpAppointment: CanvasSchedulerResponse;
  };
}

export const createCanvasFollowUpAppointmentMutation = gql`
  mutation createCanvasFollowUpAppointment(
    $input: CreateCanvasFollowUpAppointmentInput!
  ) {
    createCanvasFollowUpAppointment(input: $input) {
      success
      error {
        type
        message
        details
      }
      data {
        appointmentId
        canvasAppointmentId
        canvasPractitionerId
        appointmentDateTime
        timestamp
      }
    }
  }
`.loc?.source.body as GeneratedMutation<
  CreateCanvasFollowUpAppointmentVariables,
  {
    createCanvasFollowUpAppointment: CanvasSchedulerResponse;
  }
>;

export const useCreateCanvasFollowUpAppointment = (
  options: MutationOptions<
    CreateCanvasFollowUpAppointmentResponse,
    Error,
    CreateCanvasFollowUpAppointmentVariables
  > = {}
) => {
  const { mutateAsync, isPending } = useMutation<
    CreateCanvasFollowUpAppointmentResponse,
    Error,
    CreateCanvasFollowUpAppointmentVariables
  >({
    ...options,
    mutationFn: async (variables) =>
      fetchGraphQLClient(createCanvasFollowUpAppointmentMutation, variables),
  });

  const createCanvasFollowUpAppointment = async (
    input: CreateCanvasFollowUpAppointmentInput
  ): Promise<CanvasSchedulerResponse> => {
    const response = await mutateAsync({ input });
    const payload = response?.data?.createCanvasFollowUpAppointment;

    if (!payload) {
      throw new Error("Empty response from createCanvasFollowUpAppointment");
    }

    return payload;
  };

  return {
    createCanvasFollowUpAppointment,
    isPending,
  };
};
